import type {ItemFabricObject} from '@PosterWhiteboard/items/item/item.class';
import {Item} from '@PosterWhiteboard/items/item/item.class';
import type {BaseItemObject, CopyableItemStyles, InitItemOpts, ItemObject} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE, ItemLoadingProgressType, SHADOW_REFERENCE_DIMENSION} from '@PosterWhiteboard/items/item/item.types';
import {removeItemLoading, startItemLoading} from '@Libraries/item-loading.library';
import {MediaItemSizeType, repoURL} from '@Libraries/s3-library';
import type {Page} from '@PosterWhiteboard/page/page.class';
import type {ItemEffectsObject} from '@PosterWhiteboard/classes/item-effects.class';
import {EdgeType, ItemEffects} from '@PosterWhiteboard/classes/item-effects.class';
import {BorderType} from '@PosterWhiteboard/classes/item-border.class';
import type {ItemCropObject} from '@PosterWhiteboard/classes/item-crop.class';
import {ItemCrop} from '@PosterWhiteboard/classes/item-crop.class';
import type {RemoveImageItemBackgroundObject} from '@PosterWhiteboard/items/image-item/remove-image-item-background.class';
import {RemoveImageItemBackground} from '@PosterWhiteboard/items/image-item/remove-image-item-background.class';
import type {ItemMaskingObject} from '@PosterWhiteboard/classes/item-masking.class';
import {ItemMasking} from '@PosterWhiteboard/classes/item-masking.class';
import isEqual from 'lodash/isEqual';
import  {getUpscaledVariantBaseName, type ImageItemSourceType} from '@Libraries/image-item.library';
import {
  getDirPathForImageItem,
  getRemoveBGPostFix,
  getUrlForImageItemScreenTier,
  ImageItemSource,
  isStockImageSource,
  REMOVE_BG_POST_FIX_NAME_WITHOUT_EXTENSION,
  SCREEN_TIER_DETAILS,
  ScreenTier,
  UPSCALED_USER_IMAGE_EXTENSION,
  UPSCALED_USER_IMAGE_VARIANT_SUFFIX,
} from '@Libraries/image-item.library';
import type {MaskingObject} from '@PosterWhiteboard/classes/masking/masking.class';
import {mapNumberToRange} from '@Utils/math.util';
import {getUniqueId} from '@Utils/string.util';
import {POSTER_VERSION} from '@PosterWhiteboard/poster/poster.types';
import {cloneAsAlignedImage, getCopyProtectionOverlayImage} from '@Utils/fabric.util';
import {getWriteBucket} from '@Utils/s3.util';
import {getCompatibleImageFileExtension, hasTransparency, loadImageAsync, UserImageUploadError, UserImageUploadErrorCode} from '@Utils/image.util';
import type {RGBA} from '@Utils/color.util';
import {convertFabricColorToFillObject, rgbaArrayToRgbString, rgbaToHex} from '@Utils/color.util';
import {extractDominantColorsFromImage, scheduleDominantColorExtraction} from '@Utils/dominant-color.util';
import {FabricImage} from '@postermywall/fabricjs-2';
import type {UploadedImageData} from '@Libraries/uploading-image-library';
import {getImageDataForUploadingUidFromMap, getImageDataForUploadingUidFromServer} from '@Libraries/uploading-image-library';
import {getHashedFilenameForStockAsset, getUserImageDataWhenProcessed, triggerUserImageCreationFromAiChatImageAsset} from '@Libraries/user-image-library';
import {GROWL_TYPE} from '@Libraries/message-growl.types';
import {getPosterUi} from '@PosterWhiteboard/ui/poster-ui';
import type {DeepPartial} from '@/global';
import {EDITOR_SMALL_SCREEN_WIDTH_THRESHOLD} from '@PosterWhiteboard/poster/poster.types';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';

const TRANSPARENCY_DISABLED = 'disabled';

/** Image transparency tri-state: DISABLED when the image has no transparent areas to toggle. */
export const transparencyStates = {
  DISABLED: TRANSPARENCY_DISABLED,
  ON: 'on',
  OFF: 'off',
};

/**
 * Reference image dimension size for which all effect values (shadow, glow etc) are defined.
 */
const EFFECTS_REFERENCE_DIMENSION = 500;
const UPLOAD_TIMEOUT = 300000;
/** Seconds the on-item circular loader's fake progress is tuned to for a background stock import. */
const STOCK_UPLOAD_EXPECTED_TOTAL_TIME = 25;

interface AdditionalOldValuesForReinit {
  hasStrokeBorderType: boolean;
  solidBorderType: BorderType;
  solidBorderThickness: number;
  solidBorderColor: RGBA;
  isBackgroundRemoved: boolean;
}

export interface ImageItemObject extends BaseItemObject {
  hashedFilename?: string;
  fileExtension?: string;
  isRemoved: boolean;
  isPurchased: boolean;
  hasTransparency?: boolean;
  isUpscaled: boolean;
  showReplaceButton: boolean;
  loadedImageHeight: number;
  loadedImageWidth: number;
  removeBackground: RemoveImageItemBackgroundObject;
  cropData: ItemCropObject;
  effects: ItemEffectsObject;
  masking: ItemMaskingObject;
  imageSource: ImageItemSourceType;
  uploadingImageData?: UploadingImageData | AiChatUploadingImageData;
  transparency: string;
  attributionName: string | null;
  attributionURL: string | null;
  licenseName: string | null;
  licenseURL: string | null;
  displayAttribution: boolean;
  idUser: number | null;
}

export interface UploadingImageData {
  tempUploadingImageUID: string;
  dataUrl: string;
  uploadStartTime: number;
  stockId?: string;
}

export interface AiChatUploadingImageData extends UploadingImageData {
  aiChatImageAssetHashedId: string;
}

export class ImageItem extends Item {
  declare _fabricObject: FabricImage;
  public gitype: ITEM_TYPE = ITEM_TYPE.IMAGE;
  public hashedFilename?: string;
  public fileExtension?: string;
  public effects: ItemEffects;
  public imageSource!: ImageItemSourceType;
  public removeBackground: RemoveImageItemBackground;
  public isRemoved = false;
  public isPurchased = false;
  public showReplaceButton = false;
  public cropData: ItemCrop;
  public masking: ItemMasking;
  public imageElement!: HTMLImageElement;
  public uploadingImageData?: UploadingImageData | AiChatUploadingImageData;
  public isUploadPollingInitialized = false;
  /**
   * Dimensions of unedited image that is currently loaded
   */
  public loadedImageWidth!: number;
  public loadedImageHeight!: number;

  public hasTransparency?: boolean;
  public isUpscaled = false;
  public isAwaitingVariantUploads = false;

  public transparency: string = transparencyStates.DISABLED;
  public attributionName: string | null = null;
  public attributionURL: string | null = null;
  public licenseName: string | null = null;
  public licenseURL: string | null = null;
  public displayAttribution = true;
  public idUser: number | null = null;

  private dominantColors: RGBA[] = [];
  private dominantColorsRequestId = 0;
  private isDominantColorsDisposed = false;

  public constructor(page: Page) {
    super(page);
    this.effects = new ItemEffects(this);
    this.cropData = new ItemCrop();
    this.masking = new ItemMasking();
    this.removeBackground = new RemoveImageItemBackground(this);
  }

  override get fabricObject(): FabricImage {
    return this._fabricObject;
  }

  public override clearInProgressBackgroundRemoval(): void {
    this.removeBackground.clearInProgressState();
  }

  public override toObject(): ImageItemObject {
    return {
      ...super.toObject(),
      cropData: this.cropData.toObject(),
      effects: this.effects.toObject(),
      hashedFilename: this.hashedFilename,
      fileExtension: this.fileExtension,
      isRemoved: this.isRemoved,
      isPurchased: this.isPurchased,
      removeBackground: this.removeBackground.toObject(),
      masking: this.masking.toObject(),
      showReplaceButton: this.showReplaceButton,
      imageSource: this.imageSource,
      hasTransparency: this.hasTransparency,
      isUpscaled: this.isUpscaled,
      loadedImageHeight: this.loadedImageHeight,
      loadedImageWidth: this.loadedImageWidth,
      uploadingImageData: this.uploadingImageData ? {...this.uploadingImageData} : undefined,
      transparency: this.transparency,
      attributionName: this.attributionName,
      attributionURL: this.attributionURL,
      licenseName: this.licenseName,
      licenseURL: this.licenseURL,
      displayAttribution: this.displayAttribution,
      idUser: this.idUser,
    };
  }

  public override getCopyableStyles(): CopyableItemStyles {
    const effects: DeepPartial<ItemEffectsObject> = this.effects.toObject();
    const styles: CopyableItemStyles = {
      ...super.getCopyableStyles(),
      border: this.border.toObject(),
      textBorder: this.border.toObject(),
      effects: effects,
    };

    if (this.effects.edgeType === EdgeType.CURVED) {
      styles.rx = (this.effects.edgeThickness / 28) * 100;
      styles.ry = (this.effects.edgeThickness / 28) * 100;
    }

    if (this.imageSource === ImageItemSource.DRAWING) {
      styles.frontFill = rgbaArrayToRgbString(this.effects.tintColor);
      delete styles.effects?.edgeThickness;
      delete styles.effects?.edgeType;
    }

    return styles;
  }

  private createStylesObjForDrawing(copiedStyles: CopyableItemStyles): DeepPartial<ImageItemObject> {
    const stylesObj: DeepPartial<ImageItemObject> = {};
    if (copiedStyles.effects !== undefined) {
      stylesObj.effects = {
        ...copiedStyles.effects,
        edgeType: this.effects.edgeType,
        edgeThickness: this.effects.edgeThickness,
        tint: this.effects.tint,
        tintColor: this.effects.tintColor,
      };
    }

    if (copiedStyles.frontFill !== undefined) {
      const fillObject = convertFabricColorToFillObject(copiedStyles.frontFill);
      stylesObj.effects = {
        ...stylesObj.effects,
        tint: fillObject.fillType !== FillTypes.NONE,
        tintColor: fillObject.fillColor[0],
      };
    }

    if (copiedStyles.textBorder !== undefined) {
      stylesObj.border = {
        ...copiedStyles.textBorder,
        solidBorderType: BorderType.STROKE_BORDER,
      };
    }

    return stylesObj;
  }

  private createStylesObjForImageItem(copiedStyles: CopyableItemStyles): DeepPartial<ImageItemObject> {
    const stylesObj: DeepPartial<ImageItemObject> = {};
    if (copiedStyles.effects !== undefined) {
      stylesObj.effects = copiedStyles.effects;
    }

    if (copiedStyles.frontFill !== undefined) {
      stylesObj.effects = {
        ...stylesObj.effects,
        tint: this.effects.tint,
        tintColor: this.effects.tintColor,
      };
    }

    if (copiedStyles.rx !== undefined) {
      stylesObj.effects = {
        ...stylesObj.effects,
        edgeType: EdgeType.CURVED,
        edgeThickness: (copiedStyles.rx / 100) * 28,
      };
    }

    if (copiedStyles.textBorder !== undefined) {
      stylesObj.border = this.getBorderFromCopiedStyles(copiedStyles.textBorder);
    }
    return stylesObj;
  }

  public override async pasteStyles(copiedStyles: CopyableItemStyles): Promise<void> {
    let stylesObj: DeepPartial<ImageItemObject> = {};
    if (this.imageSource === ImageItemSource.DRAWING) {
      stylesObj = this.createStylesObjForDrawing(copiedStyles);
    } else {
      stylesObj = this.createStylesObjForImageItem(copiedStyles);
    }

    const commonStylesObj: DeepPartial<ItemObject> = this.createCommonStylesObject(copiedStyles, ['scaleX', 'scaleY']);
    await this.updateFromObject({...commonStylesObj, ...stylesObj});
  }

  public override isStrokeBorderSupported(): boolean {
    return this.isItemTransparent() || this.isIcon();
  }

  protected override getAdditionalOldValuesForReinit(): AdditionalOldValuesForReinit {
    return {
      hasStrokeBorderType: this.border.hasStrokeBorderType(),
      solidBorderType: this.border.solidBorderType,
      solidBorderThickness: this.border.solidBorderThickness,
      solidBorderColor: this.border.solidBorderColor,
      isBackgroundRemoved: this.removeBackground.isBackgroundRemoved,
    };
  }

  protected override itemObjectHasDestructiveChanges(oldItemObject: ImageItemObject, oldAdditionalValues: AdditionalOldValuesForReinit): boolean {
    return (
      this.hasStrokeBorderChanged(oldAdditionalValues) ||
      this.hasCroppingChanged(oldItemObject) ||
      this.hasRemoveBackgroundChanged(oldItemObject) ||
      this.hasEdgeTypeChanged(oldItemObject) ||
      this.hasMaskingChanged(oldItemObject) ||
      !!(!oldItemObject.hashedFilename && this.hashedFilename) ||
      !!(oldItemObject.hashedFilename && !this.hashedFilename) ||
      oldItemObject.hashedFilename !== this.hashedFilename ||
      oldItemObject.isUpscaled !== this.isUpscaled
    );
  }

  private hasEdgeTypeChanged(oldValues: ImageItemObject): boolean {
    return oldValues.effects.edgeType !== this.effects.edgeType || oldValues.effects.edgeThickness !== this.effects.edgeThickness;
  }

  private hasCroppingChanged(oldValues: ImageItemObject): boolean {
    return !isEqual(this.cropData.toObject(), oldValues.cropData);
  }

  private hasMaskingChanged(oldValues: ImageItemObject): boolean {
    return !isEqual(this.masking.toObject(), oldValues.masking);
  }

  private hasRemoveBackgroundChanged(oldValues: ImageItemObject): boolean {
    return this.removeBackground.isBackgroundRemoved !== oldValues.removeBackground.isBackgroundRemoved;
  }

  private hasStrokeBorderChanged(oldValues: AdditionalOldValuesForReinit): boolean {
    return (
      (oldValues.hasStrokeBorderType || this.border.hasStrokeBorderType()) &&
      (this.border.solidBorderType !== oldValues.solidBorderType ||
        this.border.solidBorderThickness !== oldValues.solidBorderThickness ||
        rgbaToHex(this.border.solidBorderColor) !== rgbaToHex(oldValues.solidBorderColor))
    );
  }

  public override async getFabricObjectsForPDF(): Promise<ItemFabricObject[]> {
    const objects = [];
    const multiplier = this.loadedImageWidth / this.fabricObject.getScaledWidth();

    objects.push(await cloneAsAlignedImage(this.fabricObject, {multiplier}, ['__PMWID']));
    if (this.hasGettyContent()) {
      objects.push(await getCopyProtectionOverlayImage(this.fabricObject, ['__PMWID']));
    }

    return objects;
  }

  public override copyVals(obj: DeepPartial<ImageItemObject>): void {
    const {effects, loadedImageWidth: _loadedImageWidth, loadedImageHeight: _loadedImageHeight, masking, removeBackground, cropData, ...itemObj} = obj;
    super.copyVals(itemObj);
    this.cropData.copyVals(cropData);
    this.effects.copyVals(effects);
    this.masking.copyVals(masking);
    this.removeBackground.copyVals(removeBackground);
  }

  protected override onItemDoubleClicked(): void {
    if (this.isBackground) {
      getPosterUi().onOpenImageBackgroundCropModal();
      return;
    }
    if (this.masking.maskingItem?.isText()) {
      this.openMaskingModal();
      return;
    }
    if (this.isDrawing()) {
      return;
    }
    getPosterUi().onOpenCropModal(this);
  }

  public override async updateFabricObject(): Promise<void> {
    await super.updateFabricObject();
    this.effects.applyItemEffects();
    if (this.isBackground) {
      this.scaleFabricObjectToFillPage();
    }
  }

  public isDrawing(): boolean {
    return this.imageSource === ImageItemSource.DRAWING;
  }

  public isMasked(): boolean {
    return this.masking.maskingItem !== null;
  }

  public async enableRemoveBackgroundFlag(): Promise<void> {
    await this.updateFromObject({
      removeBackground: {
        isBackgroundRemoved: true,
      },
    });
  }

  public async disableRemoveBackgroundFlag(): Promise<void> {
    await this.updateFromObject({
      removeBackground: {
        isBackgroundRemoved: false,
      },
    });
  }

  public getFileExtension(): string {
    return this.fileExtension ? getCompatibleImageFileExtension(this.fileExtension) : '';
  }

  public override isPremium(): boolean {
    return (this.isNonPurchasedGettyImage() || this.isNonPurchasedExtractedGettySticker()) && !this.isRemoved;
  }

  public hasGettyContent(): boolean {
    return (
      this.imageSource === ImageItemSource.GETTY || this.imageSource === ImageItemSource.GETTY_ILLUSTRATIONS || this.imageSource === ImageItemSource.PMW_EXTRACTED_GETTY_STICKER
    );
  }

  public isNonPurchasedGettyImage(): boolean {
    return !this.isPurchased && (this.imageSource === ImageItemSource.GETTY || this.imageSource === ImageItemSource.GETTY_ILLUSTRATIONS);
  }

  public isNonPurchasedExtractedGettySticker(): boolean {
    return !this.isPurchased && this.imageSource === ImageItemSource.PMW_EXTRACTED_GETTY_STICKER;
  }

  public getGettyImageIdentityKey(): string | undefined {
    if (this.hashedFilename) {
      return this.hashedFilename;
    }
    const stockId = this.uploadingImageData?.stockId;
    return stockId ? getHashedFilenameForStockAsset(this.imageSource, stockId) : undefined;
  }

  public isItemTransparent(): boolean {
    return this.removeBackground.isBackgroundRemoved || this.isMasked() || this.hasTransparency || this.effects.hasEdgeTypeForStroke();
  }

  public override async initFabricObject(opts: InitItemOpts = {}): Promise<void> {
    await super.initFabricObject(opts);
    if (this.isBackground) {
      this.deriveBackgroundTransparencyFromImage();
    }
  }

  protected override async onItemInitialized(): Promise<void> {
    await super.onItemInitialized();
    if (this.isBackground) {
      this.deriveBackgroundTransparencyFromImage();
    }
  }

  private deriveBackgroundTransparencyFromImage(): void {
    this.hasTransparency = this.hashedFilename && this.isJPG() ? false : hasTransparency(this.imageElement);
    this.verifyTransparencyFlags();
  }

  /** Keeps the background transparency tri-state consistent with whether the loaded image actually has transparent pixels. */
  protected verifyTransparencyFlags(): void {
    if (this.hasTransparency === false && !this.isBackgroundTransparencyDisabled()) {
      this.transparency = transparencyStates.DISABLED;
    } else if (this.hasTransparency && this.isBackgroundTransparencyDisabled()) {
      this.transparency = transparencyStates.ON;
    }
  }

  public isBackgroundImageTransparencyOn(): boolean {
    return this.transparency === transparencyStates.ON;
  }

  public isBackgroundTransparencyDisabled(): boolean {
    return this.transparency === transparencyStates.DISABLED;
  }

  public requiresAttribution(): boolean {
    return this.isBackground && !!this.attributionName && !!this.licenseName && this.imageSource !== ImageItemSource.GETTY && this.imageSource !== ImageItemSource.STORYBLOCKS;
  }

  protected async getFabricObjectForItem(opts: InitItemOpts = {}): Promise<FabricImage> {
    const targetMaxDimension = this.getTargetMaxDimension(opts);
    await this.loadImageItem(targetMaxDimension);

    return new FabricImage(this.imageElement, this.getScalesAndShadowToMaintainScaledDimensions(this.imageElement.width, this.imageElement.height, opts));
  }

  protected getTargetMaxDimension(opts: InitItemOpts = {}): number | undefined {
    const targetWidth = opts?.width && opts?.scaleX ? opts.width * opts.scaleX : undefined;
    const targetHeight = opts?.height && opts?.scaleY ? opts.height * opts.scaleY : undefined;

    return targetWidth !== undefined || targetHeight !== undefined ? Math.max(targetWidth ?? 0, targetHeight ?? 0) : undefined;
  }

  private async loadImageItem(targetMaxDimension?: number): Promise<void> {
    await this.removeBackground.ensureRemovedBackgroundImageExistsIfNeeded();
    const imageElement = await loadImageAsync(await this.getImageUrl(targetMaxDimension));
    this.loadedImageWidth = imageElement.width;
    this.loadedImageHeight = imageElement.height;
    this.imageElement = await this.applyImageTransformsBeforeInit(imageElement);
    this.initOriginalImageHasTransparency();
    this.scheduleDominantColorsUpdate();
  }

  private async applyImageTransformsBeforeInit(imageElement: HTMLImageElement): Promise<HTMLImageElement> {
    let transformedImageElement = await this.cropData.applyCropToImage(imageElement);
    transformedImageElement = await this.masking.applyMaskingToImage(transformedImageElement);
    transformedImageElement = await this.effects.applyEdgeEffectsToImageElement(transformedImageElement);
    return this.border.applyBorderBeforeInitToImageElement(transformedImageElement, this.getScaleFactorForEffects());
  }

  private scheduleDominantColorsUpdate(): void {
    if (this.page.poster.mode.isGeneration() || this.isDominantColorsDisposed) {
      return;
    }
    if (!this.hashedFilename && !this.uploadingImageData?.dataUrl) {
      return;
    }
    this.dominantColorsRequestId += 1;
    const requestId = this.dominantColorsRequestId;
    scheduleDominantColorExtraction(() => {
      if (this.isDominantColorsDisposed || this.dominantColorsRequestId !== requestId || !this.imageElement) {
        return;
      }
      const colors = extractDominantColorsFromImage(this.imageElement);
      if (colors.length === 0) {
        return;
      }
      this.dominantColors = colors;
      this.page.poster.fire('colors:extracted');
    });
  }

  private initOriginalImageHasTransparency(): void {
    if (!this.page.poster.mode.isGeneration() && !this.isTransparencyDetermined() && !this.isImageUploading()) {
      this.hasTransparency = this.isJPG() ? false : hasTransparency(this.imageElement);
    }
  }

  private isTransparencyDetermined(): boolean {
    return this.hasTransparency !== undefined;
  }

  protected isJPG(): boolean {
    return this.fileExtension ? this.fileExtension.toLowerCase() === 'jpg' : false;
  }

  protected override shouldMaintainScaledDimensionsOnReinit(oldItemObject: ImageItemObject, oldAdditionalValues: AdditionalOldValuesForReinit): boolean {
    return (
      oldAdditionalValues.isBackgroundRemoved !== this.removeBackground.isBackgroundRemoved ||
      oldItemObject.hashedFilename !== this.hashedFilename ||
      oldItemObject.isUpscaled !== this.isUpscaled
    );
  }

  public isIcon(): boolean {
    return this.imageSource === ImageItemSource.PMW_ICON;
  }

  public isAiIcon(): boolean {
    return this.imageSource === ImageItemSource.PMW_AI_ICON;
  }

  public async getImageUrl(targetMaxDimension?: number): Promise<string> {
    return this.loadHighRes() ? this.getHighresUrl(targetMaxDimension) : this.getScreenUrl();
  }

  protected repoImageURL(sizeType?: MediaItemSizeType): string {
    if (this.hashedFilename && this.getFileExtension()) {
      let imageSizeType = sizeType;
      if (imageSizeType === undefined) {
        imageSizeType = this.page.poster.isHighRes ? MediaItemSizeType.HIGH_RES : MediaItemSizeType.SCREEN;
      }
      const imageName = this.getImageS3Name();

      return repoURL(`${getDirPathForImageItem(this.imageSource, imageSizeType)}/${imageName}`, getWriteBucket());
    }
    return '';
  }

  private getImageS3Name(): string {
    if (!this.hashedFilename) {
      return ''
    }
    const upscaledVariantBaseName = getUpscaledVariantBaseName(this.hashedFilename, this.isUpscaled, this.imageSource);
    if (upscaledVariantBaseName) {
      return `${upscaledVariantBaseName}${this.getUpscaledVariantNamePostFix()}.${getCompatibleImageFileExtension(UPSCALED_USER_IMAGE_EXTENSION)}`;
    }
    if (this.removeBackground.isBackgroundRemoved) {
      return `${this.hashedFilename}${getRemoveBGPostFix()}`;
    }
    return `${this.hashedFilename}.${this.getFileExtension()}`;
  }

  protected getUpscaledVariantNamePostFix(): string {
    return `${UPSCALED_USER_IMAGE_VARIANT_SUFFIX}${this.removeBackground.isBackgroundRemoved ? REMOVE_BG_POST_FIX_NAME_WITHOUT_EXTENSION : ''}`;
  }

  protected override async fixChanges(applyVersionFixesData: Record<string, any>): Promise<void> {
    await super.fixChanges(applyVersionFixesData);
    this.fixBrightness();
    this.fixBorder();

    if (this.page.poster.version < POSTER_VERSION.HTML5 && this.border.solidBorderType !== BorderType.NONE) {
      this.fabricObject.width -= this.border.solidBorderThickness;
      this.fabricObject.height -= this.border.solidBorderThickness;
    }
  }

  protected override getOldShadowDistance(): number {
    const xDiff = this.fabricObject.width / SHADOW_REFERENCE_DIMENSION;
    const yDiff = this.fabricObject.height / SHADOW_REFERENCE_DIMENSION;
    return super.getOldShadowDistance() * Math.min(xDiff, yDiff);
  }

  /**
   * Brightness used to have a range of -100 to 100 in HTML5 posterbuilder build using fabricJs (Version: 1.5),
   * After upgrading to fabricJs (version: 2.0), new range for brightness filter is -0.4 to 0.4 hence with the upgraded
   * posterBuilder we need to map the old brightness value of images.
   */
  private fixBrightness(): void {
    if (this.page.poster.version < POSTER_VERSION.FABRIC_2_UPDATE) {
      this.effects.brightness = mapNumberToRange(this.effects.brightness, -100, 100, -0.4, 0.4);
    }
  }

  /**
   * Fix border. This bug was introduced when fabric was updated to version 3 but stroke for images was not set to uniform, so now
   * scale those borders according to image scale so that users don't see a difference
   */
  private fixBorder(): void {
    if (this.page.poster.version === POSTER_VERSION.FABRIC_3_UPDATE) {
      this.border.solidBorderThickness = Math.round(this.border.solidBorderThickness * this.fabricObject.scaleX);
    }
  }

  protected getScreenUrl(): string {
    if (this.hashedFilename && this.fileExtension) {
      return this.getDynamicSizeUrlForModel();
    }
    if (this.uploadingImageData?.dataUrl) {
      return this.uploadingImageData.dataUrl;
    }
    throw new Error(`Neither hashedFilename+Extension nor dataUrl is present`);
  }

  protected override async onItemUpdatedFromObject(): Promise<void> {
    await this.checkForUploadComplete();
    this.removeBackground.resumeBackgroundRemovalIfNeeded();
    // Init applies the saved object twice (copyVals + a trailing updateFromObject); re-derive here
    // so that trailing pass can't re-stamp a stale "disabled" over the state set during fabric init.
    if (this.isBackground) {
      this.verifyTransparencyFlags();
    }
  }

  public async checkForUploadComplete(): Promise<void> {
    if (this.hashedFilename && this.fileExtension) {
      return;
    }
    if (!this.uploadingImageData) {
      throw new Error(`Neither hashedFilename+Extension nor dataUrl is present`);
    }

    const uploadingImageData = this.uploadingImageData;
    const {tempUploadingImageUID, uploadStartTime} = uploadingImageData;

    if ('aiChatImageAssetHashedId' in uploadingImageData) {
      if (!this.isUploadPollingInitialized) {
        this.beginUploadPolling(tempUploadingImageUID);
        void this.resolveAiChatImageUpload(uploadingImageData.aiChatImageAssetHashedId, tempUploadingImageUID);
      }
      return;
    }

    const imageData = getImageDataForUploadingUidFromMap(tempUploadingImageUID);
    if (imageData) {
      await this.updateImageWithHashedFilenameAndExtension(imageData);
      return;
    }

    if (this.isUploadPollingInitialized) {
      return;
    }
    if (uploadStartTime + UPLOAD_TIMEOUT <= new Date().getTime()) {
      this.isUploadPollingInitialized = true;
      this.onUploadingImageFailed(new UserImageUploadError('uploading image time expired'));
      return;
    }
    this.beginUploadPolling(tempUploadingImageUID);
    void this.getUserImageHashedFilenameForUploadingImage();
  }

  private beginUploadPolling(tempUploadingImageUID: string): void {
    this.isUploadPollingInitialized = true;
    this.page.poster.addToUploadingImages({id: tempUploadingImageUID, source: this.imageSource}, true);
    // Stock adds land instantly with a temp data URL; the small circular loader is the only signal the
    // real import is still resolving (user uploads animate the sidebar Uploads button instead).
    if (isStockImageSource(this.imageSource)) {
      startItemLoading(this, {
        progress: {
          type: ItemLoadingProgressType.FAKE,
          expectedTotalTime: STOCK_UPLOAD_EXPECTED_TOTAL_TIME,
        },
        hideText: true,
      });
    }
  }

  private async resolveAiChatImageUpload(aiChatImageAssetHashedId: string, tempUploadingImageUID: string): Promise<void> {
    try {
      const userImageHashedFilename = await triggerUserImageCreationFromAiChatImageAsset(aiChatImageAssetHashedId, getUniqueId());
      const imageData = await getUserImageDataWhenProcessed(userImageHashedFilename);
      this.page.poster.removeFromUploadingImages(tempUploadingImageUID, true);

      if (!this.page.items.getItem(this.uid)) {
        return;
      }

      await this.updateFromObject(
        {
          hashedFilename: userImageHashedFilename,
          fileExtension: imageData.ext,
          hasTransparency: imageData.hasTransparency ?? undefined,
        },
        {undoable: false}
      );
    } catch (e) {
      this.page.poster.removeFromUploadingImages(tempUploadingImageUID, true);
      this.onUploadingImageFailed(e instanceof UserImageUploadError ? e : new UserImageUploadError('AI chat image materialization failed'));
    }
  }

  public onUploadingImageFailed(e: UserImageUploadError): void {
    console.error(e);
    let errorText = window.i18next.t('pmwjs_image_upload_error');

    if (e.errorCode) {
      switch (e.errorCode) {
        case UserImageUploadErrorCode.IMAGE_DIMENSIONS_EXCEEDED:
          errorText = window.i18next.t('pmwjs_image_upload_dimensions_exceeded_error');
          break;
        case UserImageUploadErrorCode.INVALID_IMAGE:
          errorText = window.i18next.t('pmwjs_image_upload_invalid_error');
          break;
        default:
          errorText = window.i18next.t('pmwjs_image_upload_error');
          break;
      }
    }

    getPosterUi().showMessageGrowl({
      text: errorText,
      type: GROWL_TYPE.DANGER,
    });
    this.page.items.removeItem(this.uid, false);
  }

  public async updateImageWithHashedFilenameAndExtension(imageData: UploadedImageData, undoable = false): Promise<void> {
    await this.updateFromObject(
      {
        hashedFilename: imageData.hashedFilename,
        fileExtension: imageData.fileExtension,
      },
      {undoable}
    );
    removeItemLoading(this);
  }

  public async getUserImageHashedFilenameForUploadingImage(): Promise<void> {
    try {
      await this.getUserImageHashedFilenameForUploadingImageFromServer();
    } catch (e: any) {
      if (e instanceof UserImageUploadError) {
        this.onUploadingImageFailed(e);
      } else {
        this.onUploadingImageFailed(new UserImageUploadError(e.message ?? 'Error while uploading user image. Details:', e));
      }
    }
  }

  public async getUserImageHashedFilenameForUploadingImageFromServer(): Promise<void> {
    if (this.uploadingImageData?.tempUploadingImageUID) {
      const imageData = await getImageDataForUploadingUidFromServer(this.uploadingImageData.tempUploadingImageUID);
      this.page.poster.removeFromUploadingImages(this.uploadingImageData.tempUploadingImageUID);
      await this.updateImageWithHashedFilenameAndExtension(imageData);
    }
  }

  public override onRemove(): void {
    this.isUploadPollingInitialized = false;
    super.onRemove();
    this.isDominantColorsDisposed = true;
    this.page.poster.removeFromUploadingImages(this.uid);
  }

  private getDynamicSizeUrlForModel(): string {
    const url = '';

    if (this.hashedFilename && this.getFileExtension()) {
      return this.getUrlForImageItemScreenTier(ScreenTier.BIG);
    }

    return url;
  }

  protected getUrlForImageItemScreenTier(tier: ScreenTier): string {
    if (!this.hashedFilename) {
      throw new Error(`no hashedFilename found for image with uid: ${this.uid}`);
    }
    return getUrlForImageItemScreenTier(this.hashedFilename, this.imageSource, tier, this.removeBackground.isBackgroundRemoved, this.isUpscaled);
  }

  private async getHighresUrl(targetMaxDimension?: number): Promise<string> {
    if (targetMaxDimension === undefined) {
      return this.getUnoptimizedHighresImageURL();
    }

    return this.getOptimizedHighResImageURL(targetMaxDimension * this.page.poster.scaling.scale);
  }

  private getTierForMaxImageDimension(imageMaxDimension: number): ScreenTier {
    let selectedTier = ScreenTier.SMALL;
    for (const [teirName, detail] of Object.entries(SCREEN_TIER_DETAILS)) {
      if (imageMaxDimension > detail.minTargetDimension && detail.minTargetDimension > SCREEN_TIER_DETAILS[selectedTier].minTargetDimension) {
        selectedTier = teirName as ScreenTier;
      }
    }
    return selectedTier;
  }

  private async getOptimizedHighResImageURL(targetMaxDimension: number): Promise<string> {
    // In order to get watermark free image
    if (this.hasGettyContent()) {
      return this.getDynamicalyResizedHighresImageURL(targetMaxDimension);
    }

    // getDynamicalyResizedHighresImageURL is disabled for now for drawing because the drawingToImage function doesn't return accurate image
    if (this.isDrawing()) {
      return this.getOptimizedHighResImageURLForDrawing(targetMaxDimension);
    }

    const selectedTier = this.getTierForMaxImageDimension(targetMaxDimension);
    if (targetMaxDimension <= SCREEN_TIER_DETAILS[selectedTier].maxDimension) {
      return this.getUrlForImageItemScreenTier(selectedTier);
    }

    return this.getDynamicalyResizedHighresImageURL(targetMaxDimension);
  }

  private getOptimizedHighResImageURLForDrawing(targetMaxDimension: number): string {
    const selectedTier = this.getTierForMaxImageDimension(targetMaxDimension);
    return this.getUrlForImageItemScreenTier(selectedTier);
  }

  private async getDynamicalyResizedHighresImageURL(targetMaxDimension: number): Promise<string> {
    const unoptimizedHighresImageURL = this.getUnoptimizedHighresImageURL();
    if (!this.shouldLoadDynamicallyResizedHighResImage()) {
      return unoptimizedHighresImageURL;
    }

    try {
      return (await window.PMW.writeLocal('posterimage/resizeImageToMaxDimension', {
        newMaxDimension: targetMaxDimension,
        imageUrl: unoptimizedHighresImageURL,
      })) as string;
    } catch (e) {
      return unoptimizedHighresImageURL;
    }
  }

  private shouldLoadDynamicallyResizedHighResImage(): boolean {
    return this.page.isMemoryIntensive();
  }

  private loadHighRes(): boolean {
    return this.page.poster.isHighRes || (this.page.poster.mode.isWebpage() && !this.isPremium());
  }

  public getUnoptimizedHighresImageURL(): string {
    if (this.isDrawing()) {
      return this.getUrlForImageItemScreenTier(ScreenTier.BIG);
    }
    return this.repoImageURL(MediaItemSizeType.HIGH_RES);
  }

  /**
   * Returns a scale factor so that the effects appears the same on different size images
   */
  public getScaleFactorForEffects(): number {
    const xDiff = this.loadedImageWidth / EFFECTS_REFERENCE_DIMENSION;
    const yDiff = this.loadedImageHeight / EFFECTS_REFERENCE_DIMENSION;
    return Math.min(xDiff, yDiff);
  }

  public override getColors(): RGBA[] {
    const colors: RGBA[] = super.getColors();

    if (this.effects.tint) {
      colors.push(this.effects.tintColor);
    }

    if (this.effects.multiply) {
      colors.push(this.effects.multiplyColor);
    }

    colors.push(...this.dominantColors);

    return colors;
  }

  private openMaskingModal(): void {
    void getPosterUi().onOpenMaskingModal(this);
  }

  // CodeReviewT1Later: Make changes at the place onReplaceClicked is used so that this doesn't appear as unused function
  public onReplaceClicked(): void {
    if (window.innerWidth < EDITOR_SMALL_SCREEN_WIDTH_THRESHOLD) {
      getPosterUi().openReplaceMediaModal();
    } else {
      const isPopupOpen = window.PMW.redux.store.getState().imageItemPopUpMenu.openPopup;
      getPosterUi().updateImagePopUpState({
        openPopup: !isPopupOpen,
        target: isPopupOpen ? null : this.page.activeSelection.getActiveObject(),
      });
    }
  }

  public async updateMasking(maskingObject: MaskingObject | null = null): Promise<void> {
    await this.updateFromObject({
      masking: {
        maskingItem: maskingObject,
      },
    });
  }

  public isImageUploading(): boolean {
    return this.isAwaitingVariantUploads || !!(!(this.hashedFilename && this.fileExtension) && this.uploadingImageData?.dataUrl);
  }

  public beginAwaitingVariantUploads(): void {
    this.isAwaitingVariantUploads = true;
    this.page.poster.addToUploadingImages({id: this.uid, source: this.imageSource}, true);
  }

  public endAwaitingVariantUploads(): void {
    this.isAwaitingVariantUploads = false;
    this.page.poster.removeFromUploadingImages(this.uid, true);
    if (this.imageElement && this.doesItemStillExist()) {
      this.initOriginalImageHasTransparency();
    }
  }

  doesItemStillExist(): boolean {
    if (this.isImageSlide()) {
      return Boolean(this.slideshow.slides.slidesHashMap[this.uid]);
    }
    return Boolean(this.page.items.getItem(this.uid));
  }
}
